import { useState, useCallback } from 'react';
import { API_SERVIDOR2 } from '../config/api';

interface UseAIStreamResult {
  content: string;
  isLoading: boolean;
  error: string | null;
  generate: (cnpj: string, tipo?: 'resumo' | 'comercial') => void;
  reset: () => void;
}

export const useAIStream = (): UseAIStreamResult => {
  const [content, setContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generate = useCallback(async (cnpj: string, tipo: 'resumo' | 'comercial' = 'resumo') => {
    setContent('');
    setError(null);
    setIsLoading(true);

    const endpoint = tipo === 'comercial' 
      ? `${API_SERVIDOR2.BASE}/api/v1/ia/comercial/${cnpj}`
      : `${API_SERVIDOR2.BASE}/api/v1/ia/resumo/${cnpj}`;

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('Stream não disponível');
      }

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') {
              setIsLoading(false);
              return;
            }
            try {
              const parsed = JSON.parse(data);
              if (parsed.content) {
                setContent(prev => prev + parsed.content);
              }
              if (parsed.error) {
                setError(parsed.error);
                setIsLoading(false);
                return;
              }
            } catch {
              // Ignorar linhas mal formatadas
            }
          }
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro desconhecido');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setContent('');
    setError(null);
    setIsLoading(false);
  }, []);

  return { content, isLoading, error, generate, reset };
};

export default useAIStream;
